import React, { useContext, useEffect, useState } from 'react';
import { AiOutlineFileExcel, AiOutlinePrinter } from 'react-icons/ai';
import { Link, useNavigate } from 'react-router-dom';
import { LoginContext } from '../../../reactcontext/ReactContext';
import axios from 'axios';
import { CSVLink } from 'react-csv';
import logo from '../../../assets/logo.avif';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const Collection = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const { loginInfo } = useContext(LoginContext);
  const navigate = useNavigate();

  const currentYear  = new Date().getFullYear();
  const currentMonth = MONTHS[new Date().getMonth()];

  const [groupedData, setGroupedData]   = useState({});
  const [selectedYear,  setSelectedYear]  = useState(String(currentYear));
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [loading, setLoading] = useState(true);

  useEffect(() => { !loginInfo.login && navigate('/'); }, [loginInfo]);

  useEffect(() => {
    axios.get(`${apiUrl}/payment/getGroupedPayments`, {
      headers: { loginToken: localStorage.getItem('loginToken') },
    })
    .then(res => { setGroupedData(res.data || {}); setLoading(false); })
    .catch(() => setLoading(false));
  }, []);

  const availableYears = Object.keys(groupedData).sort((a, b) => b - a);

  const monthData = groupedData[selectedYear]?.months?.[selectedMonth];
  const payments  = monthData?.payments || [];
  const total     = monthData?.total    || 0;

  function getReadableTimestamp() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
  }

  const csvData = payments.map(p => ({
    date:      p.dateOfPayment,
    amount:    parseFloat(p.amount || 0).toFixed(2),
    mode:      p.paymentMode || 'N/A',
    reference: p.referenceNumber || 'N/A',
  }));

  const generateReport = () => {
    if (!payments.length) { alert('No data to print.'); return; }
    const html2pdf = require('html2pdf.js');
    const tdStyle  = 'padding:4px 8px;border:1px solid #e5e7eb;';
    const rows = payments.map((p, i) => `
      <tr style="background:${i % 2 === 0 ? '#fff' : '#f9fafb'}">
        <td style="${tdStyle}">${i + 1}</td>
        <td style="${tdStyle}">${p.dateOfPayment || 'N/A'}</td>
        <td style="${tdStyle}">RM ${parseFloat(p.amount || 0).toFixed(2)}</td>
        <td style="${tdStyle}">${p.paymentMode || 'N/A'}</td>
        <td style="${tdStyle}">${p.referenceNumber || 'N/A'}</td>
      </tr>`).join('');
    const el = document.createElement('div');
    el.style.cssText = 'padding:20px;font-family:Arial,sans-serif;font-size:11px;';
    el.innerHTML = `
      <div style="text-align:center;margin-bottom:14px;">
        <img src="${logo}" style="width:60px;display:block;margin:0 auto 6px auto;" crossorigin="anonymous"/>
        <div style="font-size:16px;font-weight:bold;">Rahman Group</div>
        <div style="font-size:13px;margin:4px 0;">Collection Report — ${selectedMonth} ${selectedYear}</div>
      </div>
      <div style="display:flex;gap:30px;flex-wrap:wrap;margin-bottom:12px;">
        <span><em>Printed By:</em> <strong>${loginInfo.userDetails.firstName} ${loginInfo.userDetails.lastName}</strong></span>
        <span><em>Printed On:</em> <strong>${getReadableTimestamp()}</strong></span>
        <span><em>Total Records:</em> <strong>${payments.length}</strong></span>
        <span><em>Total Amount:</em> <strong>RM ${parseFloat(total).toLocaleString()}</strong></span>
        <span><em>Signature:</em> <strong>______________</strong></span>
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:10px;">
        <thead><tr style="background:#e5e7eb;">
          <th style="padding:5px 8px;border:1px solid #d1d5db;text-align:left;">#</th>
          <th style="padding:5px 8px;border:1px solid #d1d5db;text-align:left;">Date</th>
          <th style="padding:5px 8px;border:1px solid #d1d5db;text-align:left;">Amount</th>
          <th style="padding:5px 8px;border:1px solid #d1d5db;text-align:left;">Mode</th>
          <th style="padding:5px 8px;border:1px solid #d1d5db;text-align:left;">Reference</th>
        </tr></thead>
        <tbody>${rows}</tbody>
        <tfoot>
          <tr style="background:#f3f4f6;font-weight:bold;">
            <td colspan="2" style="${tdStyle}">Total</td>
            <td style="${tdStyle}">RM ${parseFloat(total).toLocaleString()}</td>
            <td colspan="2" style="${tdStyle}"></td>
          </tr>
        </tfoot>
      </table>`;
    html2pdf().set({
      margin: 10,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
    }).from(el).outputPdf('blob').then(blob => window.open(URL.createObjectURL(blob), '_blank'));
  };

  return (
    <div className='w-full flex h-full flex-col items-center relative gap-3 p-4'>
      {/* Breadcrumb */}
      <div className='self-start'>
        <Link to='/dashboard' className='text-yellow-700'>Dashboard</Link>
        {' > '}
        <Link to='/reports' className='text-yellow-700'>Reports</Link>
        {' > '} Collection
      </div>

      <h1 className='text-xl font-bold'>Collection Report</h1>

      {/* Filters */}
      <div className='flex flex-wrap gap-4 items-center bg-white shadow rounded-lg p-4 w-full'>
        <div className='flex flex-col gap-1'>
          <label className='text-xs font-semibold text-gray-600 uppercase'>Year</label>
          <select
            className='p-2 border rounded bg-gray-50 text-sm'
            value={selectedYear}
            onChange={e => setSelectedYear(e.target.value)}
          >
            {availableYears.length > 0
              ? availableYears.map(y => <option key={y} value={y}>{y}</option>)
              : <option value={currentYear}>{currentYear}</option>
            }
          </select>
        </div>
        <div className='flex flex-col gap-1'>
          <label className='text-xs font-semibold text-gray-600 uppercase'>Month</label>
          <select
            className='p-2 border rounded bg-gray-50 text-sm'
            value={selectedMonth}
            onChange={e => setSelectedMonth(e.target.value)}
          >
            {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        {/* Summary badge */}
        <div className='ml-auto flex flex-col items-end'>
          <span className='text-xs text-gray-500'>Total Collection</span>
          <span className='text-2xl font-bold text-green-600'>RM {parseFloat(total).toLocaleString()}</span>
          <span className='text-xs text-gray-400'>{payments.length} payment{payments.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Action buttons */}
      <div className='flex gap-2 w-full'>
        <button
          onClick={generateReport}
          disabled={loading || !payments.length}
          className={`flex items-center gap-1 px-4 py-2 rounded-md shadow text-sm font-medium ${
            !payments.length ? 'bg-gray-300 cursor-not-allowed text-gray-500' : 'bg-yellow-400 hover:bg-yellow-500'
          }`}
        >
          <AiOutlinePrinter fontSize={18} /> Print
        </button>
        <CSVLink
          data={csvData}
          filename={`collection-${selectedMonth}-${selectedYear}.csv`}
          className={`flex items-center gap-1 px-4 py-2 rounded-md shadow text-sm font-medium ${
            !payments.length ? 'bg-gray-300 cursor-not-allowed text-gray-500 pointer-events-none' : 'bg-gray-400 hover:bg-gray-500 text-white'
          }`}
        >
          <AiOutlineFileExcel fontSize={18} /> Export CSV
        </CSVLink>
      </div>

      {/* Table */}
      <div className='w-full bg-white shadow rounded-lg overflow-x-auto'>
        {loading ? (
          <p className='text-center py-10 text-gray-500'>Loading...</p>
        ) : payments.length === 0 ? (
          <p className='text-center py-10 text-gray-400'>
            No payments found for <strong>{selectedMonth} {selectedYear}</strong>
          </p>
        ) : (
          <table className='w-full text-sm border-collapse'>
            <thead>
              <tr className='bg-gray-100 text-gray-600 uppercase text-xs'>
                <th className='px-4 py-3 text-left border-b'>#</th>
                <th className='px-4 py-3 text-left border-b'>Date</th>
                <th className='px-4 py-3 text-left border-b'>Amount</th>
                <th className='px-4 py-3 text-left border-b'>Mode</th>
                <th className='px-4 py-3 text-left border-b'>Reference</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p, i) => (
                <tr key={p.id || i} className='border-t border-gray-100 hover:bg-gray-50'>
                  <td className='px-4 py-3 text-gray-500'>{i + 1}</td>
                  <td className='px-4 py-3'>{p.dateOfPayment || 'N/A'}</td>
                  <td className='px-4 py-3 font-semibold text-green-700'>RM {parseFloat(p.amount || 0).toLocaleString()}</td>
                  <td className='px-4 py-3'>{p.paymentMode || 'N/A'}</td>
                  <td className='px-4 py-3 text-gray-500'>{p.referenceNumber || '—'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className='bg-green-50 font-bold border-t-2 border-green-200'>
                <td colSpan={2} className='px-4 py-3 text-gray-700'>Total</td>
                <td className='px-4 py-3 text-green-700'>RM {parseFloat(total).toLocaleString()}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
};

export default Collection;
